import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loader, Button } from "@/components/PageUI";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Swords, Plus, Trophy, BarChart3 } from "lucide-react";

export default function Matches() {
  const { sportMeta } = useAuth();
  const [matches, setMatches] = useState(null);
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [open, setOpen] = useState(false);
  const [statMatch, setStatMatch] = useState(null);
  const [form, setForm] = useState({ team_id: "", opponent: "", location: "", date: "", status: "scheduled" });
  const [statForm, setStatForm] = useState({ player_id: "", stats: {} });

  const load = () => api.get("/matches").then((r) => setMatches(r.data)).catch(() => setMatches([]));
  useEffect(() => {
    load();
    api.get("/teams").then((r) => setTeams(r.data));
    api.get("/players").then((r) => setPlayers(r.data));
  }, []);

  const statFields = sportMeta?.stats || ["Goals", "Assists"];

  const create = async () => {
    if (!form.team_id || !form.opponent) return toast.error("Team and opponent required");
    try { await api.post("/matches", form); toast.success("Match scheduled"); setOpen(false); setForm({ team_id: "", opponent: "", location: "", date: "", status: "scheduled" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const complete = async (m) => {
    const hs = prompt("Home score?"); const as_ = prompt("Away score?");
    if (hs === null || as_ === null) return;
    await api.put(`/matches/${m.id}`, { ...m, home_score: +hs, away_score: +as_, status: "completed" });
    toast.success("Result saved"); load();
  };

  const saveStats = async () => {
    if (!statForm.player_id) return toast.error("Select an athlete");
    const stats = {}; statFields.forEach((f) => { stats[f] = statForm.stats[f] ? +statForm.stats[f] : 0; });
    await api.post(`/matches/${statMatch}/stats`, { player_id: statForm.player_id, stats });
    toast.success("Stats recorded"); setStatMatch(null); setStatForm({ player_id: "", stats: {} }); load();
  };

  const teamName = (id) => teams.find((t) => t.id === id)?.name || "—";
  if (!matches) return <Loader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Matches" subtitle="Schedule games, record scores, MVP and statistics." testid="matches-header"
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="brand-gradient text-white border-0 hover:opacity-90" disabled={!teams.length} data-testid="add-match-btn"><Plus size={16} className="mr-1.5" /> Schedule</Button></DialogTrigger>
            <DialogContent className="w-[calc(100%-1.5rem)] max-w-lg rounded-2xl" data-testid="match-dialog">
              <DialogHeader><DialogTitle>Schedule match</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div><Label>Team</Label>
                  <Select value={form.team_id} onValueChange={(v) => setForm({ ...form, team_id: v })}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="match-team-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Opponent</Label><Input value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} className="mt-1.5 h-11" data-testid="match-opponent-input" /></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1.5 h-11" /></div>
                  <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5 h-11" /></div>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="brand-gradient text-white border-0" data-testid="save-match-btn">Schedule</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        } />

      {matches.length === 0 ? (
        <EmptyState icon={Swords} title="No matches scheduled" desc="Schedule your first game." testid="empty-matches" />
      ) : (
        <div className="space-y-3">
          {matches.map((m) => (
            <div key={m.id} className="rounded-2xl border border-border bg-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid={`match-card-${m.id}`}>
              <div className="flex items-center gap-4">
                <div className="h-11 w-11 rounded-xl bg-accent flex items-center justify-center"><Swords size={18} className="text-primary" /></div>
                <div>
                  <p className="font-semibold">{teamName(m.team_id)} <span className="text-muted-foreground">vs</span> {m.opponent}</p>
                  <p className="text-sm text-muted-foreground">{m.date || "TBD"} · {m.location || "—"}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {m.status === "completed" ? (
                  <span className="font-heading font-bold text-lg" data-testid={`match-score-${m.id}`}>{m.home_score} - {m.away_score}</span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => complete(m)} data-testid={`complete-match-${m.id}`}><Trophy size={14} className="mr-1.5" /> Add result</Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setStatMatch(m.id)} data-testid={`stats-match-${m.id}`}><BarChart3 size={14} className="mr-1.5" /> Stats</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!statMatch} onOpenChange={(v) => !v && setStatMatch(null)}>
        <DialogContent data-testid="stats-dialog">
          <DialogHeader><DialogTitle>Record {sportMeta?.label} stats</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Athlete</Label>
              <Select value={statForm.player_id} onValueChange={(v) => setStatForm({ ...statForm, player_id: v })}>
                <SelectTrigger className="mt-1.5" data-testid="stats-player-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{players.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {statFields.map((f) => (
                <div key={f}><Label>{f}</Label><Input type="number" value={statForm.stats[f] || ""} onChange={(e) => setStatForm({ ...statForm, stats: { ...statForm.stats, [f]: e.target.value } })} className="mt-1.5" data-testid={`stat-input-${f}`} /></div>
              ))}
            </div>
          </div>
          <DialogFooter><Button onClick={saveStats} className="brand-gradient text-white border-0" data-testid="save-stats-btn">Save stats</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
